import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, 
  ShoppingBag, 
  Heart, 
  Package, 
  Tag, 
  Award, 
  Settings, 
  Upload,
  Gift,
  Image,
  FileText,
  Bell,
  HelpCircle,
  BarChart3,
  TrendingUp,
  Star,
  AlertTriangle,
  Activity,
  Building2,
  Percent,
  MapPin,
  Users,
  Cog,
  Mail
} from 'lucide-react';

interface MenuItem {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  path: string;
  color: string;
}

interface MenuSection {
  id: string;
  title: string;
  items: MenuItem[];
}

const menuData: MenuSection[] = [
  {
    id: 'orders-sales',
    title: 'Orders & Sales',
    items: [
      { id: 'orders', name: 'Orders', icon: ShoppingCart, path: '/orders', color: '#e74c3c' },
      { id: 'carts', name: 'Carts', icon: ShoppingBag, path: '/carts', color: '#f39c12' },
      { id: 'wishlist', name: 'Wishlist', icon: Heart, path: '/wishlist', color: '#e91e63' }
    ]
  },
  {
    id: 'catalog-management',
    title: 'Catalog Management',
    items: [
      { id: 'products', name: 'Products', icon: Package, path: '/product-list', color: '#3498db' },
      { id: 'categories', name: 'Categories', icon: Tag, path: '/category-list', color: '#2ecc71' },
      { id: 'brands', name: 'Brands', icon: Award, path: '/brands-list', color: '#9b59b6' },
      { id: 'product-attributes', name: 'Product Attributes', icon: Settings, path: '/attribute-list', color: '#1abc9c' },
      { id: 'attribute-values', name: 'Attribute Values', icon: Settings, path: '/attribute-value-list', color: '#34495e' },
      { id: 'sizes', name: 'Sizes', icon: Settings, path: '/product-size-list', color: '#f1c40f' },
      { id: 'bulk-upload', name: 'Bulk Product Upload', icon: Upload, path: '/bulk-upload-product', color: '#e67e22' }
    ]
  },
  {
    id: 'business-tools',
    title: 'Business Tools',
    items: [
      { id: 'coupons', name: 'Coupons', icon: Gift, path: '/coupon-list', color: '#27ae60' },
      { id: 'banners', name: 'Banners', icon: Image, path: '/banner-list', color: '#8e44ad' },
      { id: 'cms-pages', name: 'CMS Pages', icon: FileText, path: '/cms-list', color: '#16a085' },
      { id: 'notifications', name: 'Notifications', icon: Bell, path: '/announcement-list', color: '#d35400' },
      { id: 'faqs', name: 'FAQs', icon: HelpCircle, path: '/faq-list', color: '#7f8c8d' }
    ]
  },
  {
    id: 'reports-analytics',
    title: 'Reports & Analytics',
    items: [
      { id: 'sales-summary', name: 'Sales Summary', icon: BarChart3, path: '/dashboard', color: '#2980b9' },
      { id: 'orders-by-sales-rep', name: 'Orders by Sales Rep', icon: TrendingUp, path: '/orders-by-sales-rep', color: '#c0392b' },
      { id: 'best-sellers', name: 'Best Sellers', icon: Star, path: '/dashboard', color: '#f39c12' },
      { id: 'products-never-sold', name: 'Products Never Sold', icon: AlertTriangle, path: '/dashboard', color: '#95a5a6' },
      { id: 'low-inventory', name: 'Low Inventory', icon: AlertTriangle, path: '/dashboard', color: '#e74c3c' },
      { id: 'track-activity', name: 'Track Activity', icon: Activity, path: '/dashboard', color: '#2c3e50' }
    ]
  },
  {
    id: 'master-data-configuration',
    title: 'Master Data & Configuration',
    items: [
      { id: 'manage-company', name: 'Manage Company', icon: Building2, path: '/manage-company', color: '#8e44ad' },
      { id: 'gst-tax-rates', name: 'GST / Tax Rates', icon: Percent, path: '/gstrates', color: '#27ae60' },
      { id: 'districts-cities', name: 'Districts & Cities', icon: MapPin, path: '/district-list', color: '#3498db' },
      { id: 'users-roles', name: 'Users / Roles', icon: Users, path: '/user-roles', color: '#e67e22' },
      { id: 'common-settings', name: 'Common Settings', icon: Cog, path: '/setting-list', color: '#34495e' },
      { id: 'email-settings', name: 'Email Settings', icon: Mail, path: '/setting-list', color: '#16a085' }
    ]
  }
];

const DeskViewDashboard: React.FC = () => {
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0"></h4>
                     </div>
        </div>
      </div>


      <div className="row mt-4">
        {menuData.map((section) => (
          <div key={section.id} className="col-12 mb-5">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-header bg-white border-0 pb-0" style={{ borderRadius: '12px 12px 0 0', padding: '1rem 1.25rem' }}>
                <h5 className="card-title mb-0 fw-semibold text-dark">{section.title}</h5>
              </div>
              <div className="card-body bg-white pt-3" style={{ background: 'linear-gradient(135deg, #6e8efb, #a777e3)' }}>
                <div 
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                  }}>
                  {section.items.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <div key={item.id}>
                        <div className="d-flex flex-column align-items-center text-center w-[150px]">
                          <Link 
                            to={item.path} 
                            className="text-decoration-none"
                          >
                            <div 
                                 style={{
                                   transition: 'all 0.2s ease',
                                   cursor: 'pointer',
                                   borderRadius: '8px',
                                   backgroundColor: item.color,
                                   border: '1px solid #e9ecef',
                                   width: '70px',
                                   height: '70px',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   margin: '0 auto'
                                 }}
                                 onMouseEnter={(e) => {
                                   e.currentTarget.style.transform = 'translateY(-2px)';
                                   e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                   e.currentTarget.style.backgroundColor = item.color + '40'; // Add 40 for 25% opacity
                                   // Change icon color to vibrant color on hover
                                   const icon = e.currentTarget.querySelector('svg');
                                   if (icon) icon.style.color = item.color;
                                 }}
                                 onMouseLeave={(e) => {
                                   e.currentTarget.style.transform = 'translateY(0)';
                                   e.currentTarget.style.boxShadow = 'none';
                                   e.currentTarget.style.backgroundColor = item.color;
                                   // Change icon color back to white
                                   const icon = e.currentTarget.querySelector('svg');
                                   if (icon) icon.style.color = 'white';
                                 }}>
                              <IconComponent style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                          </Link>
                          <span className="fw-medium mt-2 text-center text-bold" style={{ fontSize: '0.8rem', lineHeight: '1.2', color: '#ffffff', display: 'block', width: '100%' }}>
                            {item.name}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeskViewDashboard;
